import React, { useCallback, useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { isAdmin } from "../../../redux/config";
import InnerLayout from "../../../components/layouts/inner-layout";
import { DataContext } from "../../../components/layouts/main-layout";
import successToast from "../../../components/helper-components/success-toast";
import errorToast from "../../../components/helper-components/error-toast";
import {
  schemeListAction,
  schemeReorderAction,
  schemeCreateAction,
  schemeUpdateAction,
  schemePauseAction,
  schemeResumeAction,
  schemeArchiveAction,
} from "../../../redux/actions/scheme-action";
import SubTabs from "./components/sub-tabs";
import OverviewTab from "./tabs/overview-tab";
import ListTab from "./tabs/list-tab";
import CreateTab from "./tabs/create-tab";

const HASH_TAB = (h) => {
  const v = (h || "").replace("#", "");
  return ["overview", "list", "create"].includes(v) ? v : "overview";
};

const SchemesAdmin = ({ embedded = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { permissionFn } = useContext(DataContext);

  const [tab, setTab] = useState(() => HASH_TAB(window.location.hash));
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Scheme being edited (null = create-new mode). Clicking a row in list-tab
  // sets this and switches the user to the create tab, which now doubles as
  // the edit form.
  const [editingScheme, setEditingScheme] = useState(null);

  // CPOS-only writes; POS is read-only. Reuses the existing isAdmin flag
  // (true on CPOS, false on POS) from redux/config.
  const readOnly = !isAdmin;

  const perm = permissionFn ? permissionFn("schemes_admin") || {} : { all: true };

  const loadList = useCallback(() => {
    setLoading(true);
    dispatch(
      schemeListAction(
        {},
        (res) => {
          setLoading(false);
          const rows = Array.isArray(res?.data) ? res.data : res?.data?.rows || [];
          setSchemes(rows);
        },
        () => {
          setLoading(false);
          errorToast("Failed to load schemes");
        }
      )
    );
  }, [dispatch]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    const onHash = () => setTab(HASH_TAB(window.location.hash));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const goTo = (t) => {
    setTab(t);
    window.location.hash = t;
    document.querySelector(".schemes-scroller")?.scrollTo(0, 0);
  };

  // Reorder — list-tab fires this with the new full ordering. We map index → priority
  // (top = highest), then send the bulk transaction. Higher number = applied first.
  const handleReorder = (orderedIds) => {
    if (readOnly) return;
    const order = orderedIds.map((id, idx) => ({
      id,
      priority: (orderedIds.length - idx) * 10,
    }));
    // optimistic local update so the drag handle feels instant
    const byId = new Map(schemes.map((s) => [s.id, s]));
    const next = order
      .map(({ id, priority }) => ({ ...byId.get(id), priority }))
      .filter(Boolean);
    setSchemes(next);
    dispatch(
      schemeReorderAction(
        { order },
        () => successToast("Scheme priorities updated"),
        (err) => {
          errorToast(err?.message || "Reorder failed");
          loadList();
        }
      )
    );
  };

  const handleStatusChange = (id, nextStatus) => {
    if (readOnly) return;
    const actionMap = {
      paused: schemePauseAction,
      active: schemeResumeAction,
      archived: schemeArchiveAction,
    };
    const fn = actionMap[nextStatus];
    if (!fn) return;
    dispatch(
      fn(
        { id, body: {} },
        () => {
          successToast(`Scheme ${nextStatus}`);
          loadList();
        },
        (err) => errorToast(err?.message || "Update failed")
      )
    );
  };

  // Single save handler covering both create (no editingScheme) and edit
  // (editingScheme set). The save endpoint is PUT /:id when editing.
  const handleCreate = (payload) => {
    if (readOnly) return;
    setSaving(true);
    const isEdit = !!editingScheme?.id;
    const action = isEdit
      ? schemeUpdateAction({ id: editingScheme.id, body: payload },
          () => {
            setSaving(false);
            successToast("Scheme updated");
            setEditingScheme(null);
            loadList();
            goTo("list");
          },
          (err) => {
            setSaving(false);
            errorToast(err?.message || "Update failed");
          })
      : schemeCreateAction(payload,
          () => {
            setSaving(false);
            successToast("Scheme created");
            loadList();
            goTo("list");
          },
          (err) => {
            setSaving(false);
            errorToast(err?.message || "Save failed");
          });
    dispatch(action);
  };

  const handleEditClick = (scheme) => {
    if (readOnly) return;
    setEditingScheme(scheme);
    goTo("create");
  };

  if (perm.all === false) {
    const denied = (
      <div className="h-full flex items-center justify-center text-[#64748B]">
        You don't have permission to view Schemes Admin.
      </div>
    );
    return embedded ? denied : <InnerLayout title="Settings">{denied}</InnerLayout>;
  }

  const body = (
    <div className={`flex flex-col ${embedded ? "" : "h-full bg-[#FAFBFC]"}`}>
      {!embedded && (
        <div className="flex items-center gap-2 px-6 py-3 text-[13px] text-[#475467]">
          <button
            className="flex items-center gap-1 hover:text-[#0F172A]"
            onClick={() => navigate("/settings")}
          >
            <ChevronLeftIcon className="h-4 w-4" />
            Discount Settings
          </button>
          <span>/</span>
          <span className="text-[#0F172A] font-medium">Schemes</span>
          {readOnly && (
            <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#FFF3D6] text-[#9A6700]">
              Read-only at POS — edit in CPOS
            </span>
          )}
        </div>
      )}

      <SubTabs active={tab} onChange={goTo} />

        <div className="schemes-scroller flex-1 overflow-y-auto">
          <div className="px-6 py-5 max-w-[1400px]">
            {tab === "overview" ? (
              <OverviewTab schemes={schemes} loading={loading} goTo={goTo} />
            ) : tab === "list" ? (
              <ListTab
                schemes={schemes}
                loading={loading}
                readOnly={readOnly}
                onReorder={handleReorder}
                onStatusChange={handleStatusChange}
                onEdit={handleEditClick}
                onCreate={() => { setEditingScheme(null); goTo("create"); }}
              />
            ) : tab === "create" ? (
              <CreateTab
                readOnly={readOnly}
                saving={saving}
                editing={editingScheme}
                onCancel={() => { setEditingScheme(null); goTo("list"); }}
                onSave={handleCreate}
              />
            ) : null}
          </div>
        </div>
    </div>
  );

  return embedded ? body : <InnerLayout title="Settings">{body}</InnerLayout>;
};

export default SchemesAdmin;
